import { useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Loader2, AlertTriangle, Phone, Mail, MapPin, Clock, MessageCircle, Linkedin } from "lucide-react";
import { Breadcrumbs, PrimaryButton } from "../components/common";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { BUSINESS, SERVICE_GROUPS } from "../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ALL_SERVICES = SERVICE_GROUPS.flatMap((g) => g.items.map((i) => i.title)).filter((v, i, a) => a.indexOf(v) === i);

export default function Contact() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const kind = params.get("type") === "sample" ? "sample" : "quote";
  const [form, setForm] = useState({
    full_name: "", organisation: "", email: "", phone: "", country: "",
    service: "", volume: "", turnaround: "", message: "", preferred_contact: "Email", consent: false,
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setErrors((e) => ({ ...e, [k]: undefined })); };

  const validate = () => {
    const e = {};
    if (!form.full_name.trim()) e.full_name = "Please enter your name.";
    if (!form.email.trim()) e.email = "Please enter your email.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Please enter a valid email.";
    if (!form.service) e.service = "Please select a service.";
    if (!form.message.trim()) e.message = "Please describe your project.";
    if (!form.consent) e.consent = "Consent is required to proceed.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) { toast.error("Please correct the highlighted fields."); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/enquiries`, { ...form, kind });
      navigate("/thank-you");
    } catch (err) {
      toast.error("Something went wrong. Your details are preserved—please try again.");
    } finally {
      setLoading(false);
    }
  };

  const waLink = `https://wa.me/${BUSINESS.whatsapp.replace(/[^0-9]/g, "")}`;
  const contacts = [
    { icon: Phone, label: "Phone", value: BUSINESS.phone, href: `tel:${BUSINESS.phone.replace(/[^0-9+]/g, "")}` },
    { icon: MessageCircle, label: "WhatsApp", value: BUSINESS.whatsapp, href: waLink, external: true },
    { icon: Mail, label: "Email", value: BUSINESS.email, href: `mailto:${BUSINESS.email}` },
    { icon: Linkedin, label: "LinkedIn", value: "linkedin.com/in/manieshsrivastva", href: BUSINESS.linkedinProfile, external: true },
    { icon: MapPin, label: "Address", value: BUSINESS.address },
    { icon: Clock, label: "Hours", value: BUSINESS.hours },
  ];

  return (
    <>
      <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Contact" }]} />
      <section className="container-page py-12 lg:py-16">
        <span className="eyebrow">Contact & Request a Quote</span>
        <h1 className="mt-4 max-w-3xl font-heading text-[34px] font-800 leading-[1.1] text-brand-ink md:text-[52px]">Based in New Delhi. Supporting clients remotely.</h1>
        <p className="mt-4 max-w-2xl text-[17px] text-brand-muted md:text-[18px]">Tell us about your documentation needs and we will review the requirement and recommend a practical next step.</p>

        <div className="mt-12 grid gap-12 lg:grid-cols-[1.3fr_0.7fr]">
          <div>
            <div className="mb-6 flex items-start gap-3 rounded-xl border border-brand-warning/30 bg-[#FBF6EC] p-4 text-[13.5px] text-brand-ink" data-testid="confidential-notice">
              <AlertTriangle className="mt-0.5 h-4.5 w-4.5 shrink-0 text-brand-warning" style={{ width: 18, height: 18 }} />
              <span>Please do not submit patient information, confidential legal records or commercially sensitive files through this form. Secure transfer arrangements can be discussed after the initial project review.</span>
            </div>

            <form onSubmit={submit} noValidate className="grid gap-5 sm:grid-cols-2" data-testid="contact-form">
              <Field label="Full name" required error={errors.full_name}>
                <Input value={form.full_name} onChange={(e) => set("full_name", e.target.value)} data-testid="field-full-name" aria-invalid={!!errors.full_name} />
              </Field>
              <Field label="Organisation">
                <Input value={form.organisation} onChange={(e) => set("organisation", e.target.value)} data-testid="field-organisation" />
              </Field>
              <Field label="Work email" required error={errors.email}>
                <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="field-email" aria-invalid={!!errors.email} />
              </Field>
              <Field label="Phone (optional)">
                <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="field-phone" />
              </Field>
              <Field label="Country">
                <Input value={form.country} onChange={(e) => set("country", e.target.value)} data-testid="field-country" />
              </Field>
              <Field label="Service required" required error={errors.service}>
                <Select value={form.service} onValueChange={(v) => set("service", v)}>
                  <SelectTrigger data-testid="field-service" aria-invalid={!!errors.service}><SelectValue placeholder="Select a service" /></SelectTrigger>
                  <SelectContent>
                    {ALL_SERVICES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Approximate volume">
                <Input value={form.volume} onChange={(e) => set("volume", e.target.value)} placeholder="e.g. 50 files / month" data-testid="field-volume" />
              </Field>
              <Field label="Required turnaround">
                <Input value={form.turnaround} onChange={(e) => set("turnaround", e.target.value)} placeholder="e.g. 48 hours" data-testid="field-turnaround" />
              </Field>
              <div className="sm:col-span-2">
                <Field label="Project description" required error={errors.message}>
                  <Textarea rows={5} value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="Describe the work, where quality problems occur and what a successful outcome looks like." data-testid="field-message" aria-invalid={!!errors.message} />
                </Field>
              </div>
              <Field label="Preferred contact method">
                <Select value={form.preferred_contact} onValueChange={(v) => set("preferred_contact", v)}>
                  <SelectTrigger data-testid="field-preferred-contact"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Email">Email</SelectItem>
                    <SelectItem value="Phone">Phone</SelectItem>
                    <SelectItem value="WhatsApp">WhatsApp</SelectItem>
                  </SelectContent>
                </Select>
              </Field>

              <div className="sm:col-span-2">
                <label className="flex items-start gap-3 text-[14px] text-brand-muted">
                  <Checkbox checked={form.consent} onCheckedChange={(v) => set("consent", !!v)} data-testid="field-consent" className="mt-0.5" />
                  <span>I consent to Primevanta Global contacting me about this enquiry and confirm I have not included confidential records.{errors.consent && <span className="mt-1 block text-brand-error">{errors.consent}</span>}</span>
                </label>
              </div>

              <div className="sm:col-span-2">
                <button type="submit" disabled={loading} className="group inline-flex items-center justify-center gap-2 rounded-full bg-brand-primary px-8 py-4 font-heading font-600 text-[15px] text-white transition-colors hover:bg-[#0F5541] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold focus-visible:ring-offset-2 disabled:opacity-70" data-testid="contact-submit">
                  {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending…</> : (kind === "sample" ? "Request a Sample" : "Submit Enquiry")}
                </button>
                <p className="mt-3 text-[12px] text-brand-muted">Protected against spam. We typically respond by your preferred method.</p>
              </div>
            </form>
          </div>

          <aside className="space-y-5">
            <div className="rounded-2xl border border-brand-border bg-white p-6">
              <h2 className="font-heading text-[18px] font-700 text-brand-ink">Contact details</h2>
              <ul className="mt-4 space-y-4">
                {contacts.map((c) => (
                  <li key={c.label} className="flex items-start gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand-alt text-brand-primary"><c.icon className="h-4.5 w-4.5" style={{ width: 18, height: 18 }} /></span>
                    <div>
                      <p className="text-[12px] font-600 uppercase tracking-wider text-brand-muted">{c.label}</p>
                      {c.href ? (
                        <a href={c.href} {...(c.external ? { target: "_blank", rel: "noopener noreferrer" } : {})} className="text-[15px] text-brand-ink transition-colors hover:text-brand-primary" data-testid={`contact-${c.label.toLowerCase()}`}>{c.value}</a>
                      ) : (
                        <p className="text-[15px] text-brand-ink">{c.value}</p>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
            <a href={BUSINESS.mapsUrl} target="_blank" rel="noopener noreferrer" className="group block overflow-hidden rounded-2xl border border-brand-border bg-brand-alt" data-testid="contact-map">
              <div className="flex h-48 items-center justify-center text-center text-brand-muted">
                <div><MapPin className="mx-auto mb-2 h-6 w-6 text-brand-primary transition-transform group-hover:-translate-y-0.5" /><p className="text-[13px] font-600 text-brand-ink">Dwarka, New Delhi</p><p className="text-[12px]">Open in Google Maps</p></div>
              </div>
            </a>
          </aside>
        </div>
      </section>
    </>
  );
}

function Field({ label, required, error, children }) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label className="text-[13.5px] font-600 text-brand-ink">{label}{required && <span className="text-brand-error"> *</span>}</Label>
      {children}
      {error && <span className="text-[12.5px] text-brand-error">{error}</span>}
    </div>
  );
}
